import { createEntityId } from '@/core/EntityId';
import type { EnvironmentState } from '@/environment/EnvironmentState';
import type { CameraSnapshot } from './CaptureService';
import type { ImageSize, RenderQualityTier } from './ImageSpec';
import {
  blobImage,
  type NewShot,
  type NewVariant,
  type PhotoStorage,
  type StoredShot,
} from './PhotoStorage';
import type { SceneManifest } from './SceneManifest';

/**
 * Persistent storage for photo-mode shots, on this device.
 *
 * Shots hold full-resolution PNGs, which rules out `localStorage`. IndexedDB stores `Blob` values as
 * lazy handles backed by disk, so listing the gallery reads metadata without pulling megabytes of
 * pixels into memory — the thumbnail is the only image a contact sheet actually needs.
 *
 * Variants live inside their parent shot rather than in a second store: a variant is meaningless
 * without the capture it was derived from, and one record keeps deletion atomic.
 *
 * When IndexedDB is unavailable (private mode, disabled storage) the store degrades to memory for
 * the session rather than failing the feature outright.
 *
 * **On-disk shape.** Records are stored with raw `Blob`s — {@link StoredRecord} — and wrapped into
 * the {@link StoredShot} the UI sees on the way out. Keeping the stored form free of object URLs is
 * what lets this class own their lifetimes: every URL it mints is tracked and revoked on
 * {@link PhotoStore.close}, so a long session does not accumulate one per gallery re-render the way
 * it did when the gallery minted them itself.
 */

const DATABASE_NAME = 'landscape-glade-photos';
const DATABASE_VERSION = 1;
const SHOT_STORE = 'shots';

/** A variant as it sits in IndexedDB: raw bytes, no object URL. */
interface StoredVariantRecord {
  id: string;
  createdAt: number;
  /** Style preset used, kept by id and label so the gallery reads well after a preset is renamed. */
  presetId: string;
  presetLabel: string;
  prompt: string;
  model: string;
  quality: RenderQualityTier;
  size: ImageSize;
  image: Blob;
  estimatedCostUsd: number;
}

/** A shot as it sits in IndexedDB. Unchanged from the format earlier builds wrote. */
interface StoredRecord {
  id: string;
  createdAt: number;
  title: string;
  size: ImageSize;
  beauty: Blob;
  idMap: Blob;
  depth: Blob;
  thumbnail: Blob;
  manifest: SceneManifest;
  camera: CameraSnapshot;
  environment: EnvironmentState;
  variants: StoredVariantRecord[];
}

export class PhotoStore implements PhotoStorage {
  private database: IDBDatabase | null = null;
  private opening: Promise<IDBDatabase | null> | null = null;
  /** Session fallback used when IndexedDB is unavailable. */
  private readonly memory = new Map<string, StoredRecord>();
  /** Object URLs handed out, revoked together on {@link close}. */
  private readonly issuedUrls = new Set<string>();
  private useMemory = false;

  /** Whether shots will survive a reload. False when running on the memory fallback. */
  get persistent(): boolean {
    return !this.useMemory;
  }

  async save(shot: NewShot): Promise<StoredShot> {
    const record: StoredRecord = {
      ...shot,
      id: createEntityId('shot-'),
      createdAt: Date.now(),
      variants: [],
    };
    await this.put(record);
    return this.hydrate(record);
  }

  /** All shots, newest first. */
  async list(): Promise<StoredShot[]> {
    return (await this.records())
      .sort((a, b) => b.createdAt - a.createdAt)
      .map((record) => this.hydrate(record));
  }

  async get(id: string): Promise<StoredShot | undefined> {
    const record = await this.record(id);
    return record ? this.hydrate(record) : undefined;
  }

  /** Attach an enhanced render to a shot. Returns the updated record. */
  async addVariant(shotId: string, variant: NewVariant): Promise<StoredShot | undefined> {
    const shot = await this.record(shotId);
    if (!shot) return undefined;
    const stored: StoredVariantRecord = {
      ...variant,
      id: createEntityId('variant-'),
      createdAt: Date.now(),
    };
    // Newest first, matching how the gallery reads.
    const updated: StoredRecord = { ...shot, variants: [stored, ...shot.variants] };
    await this.put(updated);
    return this.hydrate(updated);
  }

  async removeVariant(shotId: string, variantId: string): Promise<StoredShot | undefined> {
    const shot = await this.record(shotId);
    if (!shot) return undefined;
    const updated: StoredRecord = {
      ...shot,
      variants: shot.variants.filter((variant) => variant.id !== variantId),
    };
    await this.put(updated);
    return this.hydrate(updated);
  }

  async rename(shotId: string, title: string): Promise<StoredShot | undefined> {
    const shot = await this.record(shotId);
    if (!shot) return undefined;
    const updated: StoredRecord = { ...shot, title };
    await this.put(updated);
    return this.hydrate(updated);
  }

  async remove(id: string): Promise<void> {
    const database = await this.open();
    if (!database) {
      this.memory.delete(id);
      return;
    }
    await request(this.transaction(database, 'readwrite').delete(id));
  }

  async clear(): Promise<void> {
    const database = await this.open();
    if (!database) {
      this.memory.clear();
      return;
    }
    await request(this.transaction(database, 'readwrite').clear());
  }

  /** Approximate bytes held by all stored images, for a storage readout. */
  async usageBytes(): Promise<number> {
    // Reads the raw records, not `list()`: `Blob.size` is metadata IndexedDB already has, so this
    // costs nothing, whereas going through the hydrated form would mint object URLs to answer a
    // number nobody looks at twice.
    const shots = await this.records();
    return shots.reduce((total, shot) => {
      const base = shot.beauty.size + shot.idMap.size + shot.depth.size + shot.thumbnail.size;
      return total + base + shot.variants.reduce((sum, v) => sum + v.image.size, 0);
    }, 0);
  }

  close(): void {
    for (const url of this.issuedUrls) URL.revokeObjectURL(url);
    this.issuedUrls.clear();
    this.database?.close();
    this.database = null;
    this.opening = null;
  }

  /** Wrap a stored record in the lazily-addressable form the UI consumes. */
  private hydrate(record: StoredRecord): StoredShot {
    const register = (url: string): void => {
      this.issuedUrls.add(url);
    };
    return {
      id: record.id,
      createdAt: record.createdAt,
      title: record.title,
      size: record.size,
      manifest: record.manifest,
      camera: record.camera,
      environment: record.environment,
      beauty: blobImage(record.beauty, register),
      idMap: blobImage(record.idMap, register),
      depth: blobImage(record.depth, register),
      thumbnail: blobImage(record.thumbnail, register),
      variants: record.variants.map((variant) => ({
        ...variant,
        image: blobImage(variant.image, register),
      })),
    };
  }

  private async records(): Promise<StoredRecord[]> {
    const database = await this.open();
    if (!database) return [...this.memory.values()];
    return request<StoredRecord[]>(
      this.transaction(database, 'readonly').getAll() as IDBRequest<StoredRecord[]>,
    );
  }

  private async record(id: string): Promise<StoredRecord | undefined> {
    const database = await this.open();
    if (!database) return this.memory.get(id);
    return request<StoredRecord | undefined>(
      this.transaction(database, 'readonly').get(id) as IDBRequest<StoredRecord | undefined>,
    );
  }

  private async put(record: StoredRecord): Promise<void> {
    const database = await this.open();
    if (!database) {
      this.memory.set(record.id, record);
      return;
    }
    await request(this.transaction(database, 'readwrite').put(record));
  }

  private transaction(database: IDBDatabase, mode: IDBTransactionMode): IDBObjectStore {
    return database.transaction(SHOT_STORE, mode).objectStore(SHOT_STORE);
  }

  /** Open (and upgrade) the database once, memoised. Resolves null on the memory fallback. */
  private open(): Promise<IDBDatabase | null> {
    if (this.database) return Promise.resolve(this.database);
    if (this.opening) return this.opening;

    if (typeof indexedDB === 'undefined') {
      this.useMemory = true;
      return Promise.resolve(null);
    }

    this.opening = new Promise<IDBDatabase | null>((resolve) => {
      let open: IDBOpenDBRequest;
      try {
        open = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
      } catch {
        this.useMemory = true;
        resolve(null);
        return;
      }

      open.onupgradeneeded = () => {
        const database = open.result;
        if (!database.objectStoreNames.contains(SHOT_STORE)) {
          database.createObjectStore(SHOT_STORE, { keyPath: 'id' });
        }
      };
      open.onsuccess = () => {
        this.database = open.result;
        this.useMemory = false;
        resolve(this.database);
      };
      open.onerror = () => {
        // Private browsing and blocked storage both land here. Not fatal — photo mode still works,
        // the shots simply do not outlive the session.
        console.warn('Photo storage unavailable; shots will not persist.', open.error);
        this.useMemory = true;
        resolve(null);
      };
      open.onblocked = () => {
        this.useMemory = true;
        resolve(null);
      };
    });

    return this.opening;
  }
}

function request<T = unknown>(source: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    source.onsuccess = () => resolve(source.result);
    source.onerror = () => reject(source.error ?? new Error('IndexedDB request failed.'));
  });
}
